"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, Key, ShieldCheck, FileText, Ban, Layers3 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CallToAction() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="call-to-action" className="bg-background px-6 py-16 sm:py-20 lg:px-8">
      <div ref={ref} className={`mx-auto max-w-5xl transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <Card className="relative overflow-hidden border border-border/70 bg-card shadow-[0_24px_60px_-25px_rgba(0,0,0,0.18)]">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--color-accent)_0%,_transparent_80%)] opacity-60" />

          <CardContent className="relative z-10 flex flex-col items-center px-6 py-16 text-center sm:px-12 sm:py-20">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <Layers3 className="size-4" />
              <span>Deploy RAG Without Building It Yourself</span>
            </div>

            <h2 className="mb-6 max-w-3xl text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
              Deploy a business-ready RAG platform for your team or your clients
            </h2>

            <p className="mb-10 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              EloRAG gives businesses and agencies grounded answers, approved-only retrieval,
              citation-based output, and a cleaner deployment model than generic chat-with-documents tools.
            </p>

            <div className="flex w-full max-w-3xl flex-col items-center justify-center gap-4 sm:flex-row">
              <Button asChild size="lg" className="h-12 w-full px-8 text-base sm:w-auto">
                <Link to="/BookADemoContactSalesPage">
                  Book a Demo <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 w-full bg-background/50 px-8 text-base backdrop-blur-sm sm:w-auto">
                <Link to="/PricingPage">View Pricing</Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="h-12 w-full px-8 text-base sm:w-auto">
                <Link to="/WhiteLabelForAgenciesPage">
                  <SafeHydrate><Calendar className="mr-2 size-4 text-muted-foreground" /></SafeHydrate>
                  White-Label
                </Link>
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs font-medium text-muted-foreground sm:text-sm">
              <span className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary/70" />
                Approved-Only Access
              </span>
              <span className="hidden text-border sm:inline">•</span>
              <span className="flex items-center gap-2">
                <FileText className="size-4 text-primary/70" />
                Citation-Based Answers
              </span>
              <span className="hidden text-border sm:inline">•</span>
              <span className="flex items-center gap-2">
                <Ban className="size-4 text-primary/70" />
                Refusal Over Guessing
              </span>
              <span className="hidden text-border sm:inline">•</span>
              <span className="flex items-center gap-2">
                <Key className="size-4 text-primary/70" />
                BYO-Key
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}